import { useTranslation } from "react-i18next";
import { Minus, Package, Plus, ShoppingBag, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { Product, ProductQuantities } from "@/lib/types";

/** Most of one product a booking can take (the server allows 99). */
const MAX_PER_PRODUCT = 99;

/**
 * Products shown right in the booking (step 2, under the add-ons) as a row
 * of cards the customer swipes through and adds with +/−. There is no shop:
 * they are paid with the wash and the crew brings them. "See all" opens the
 * same products as a list (BookingProductsSheet).
 */
export function BookingProductsRow({
  products,
  quantities,
  onChange,
  onSeeAll,
}: {
  products: Product[];
  quantities: ProductQuantities;
  onChange: (productId: string, quantity: number) => void;
  onSeeAll?: () => void;
}) {
  const { t } = useTranslation();
  const picked = products.some((p) => (quantities[p.id] || 0) > 0);
  // Sold-out products go last so the first cards can always be added.
  const ordered = [...products].sort((a, b) => Number(b.inStock) - Number(a.inStock));

  return (
    <div data-step="products">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="min-w-0">
          <p className="text-sm font-medium flex items-center gap-2">
            <ShoppingBag className="h-4 w-4 text-primary shrink-0" />
            {t("booking.products.addTitle")}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">{t("booking.products.addHint")}</p>
        </div>
        {onSeeAll && products.length > 3 && (
          <button
            type="button"
            onClick={onSeeAll}
            className="shrink-0 text-xs font-medium text-primary hover:underline"
            data-testid="button-see-all-products"
          >
            {t("booking.products.seeAll")}
          </button>
        )}
      </div>

      {/* Bleeds to the card edges like the date row; snaps card by card. */}
      <ul
        className="flex gap-2.5 overflow-x-auto snap-x snap-mandatory scroll-px-4 -mx-4 px-4 pb-2 scrollbar-hide"
        data-testid="booking-products-row"
      >
        {ordered.map((product) => (
          <ProductCard
            key={product.id}
            product={product}
            quantity={quantities[product.id] || 0}
            onChange={(quantity) => onChange(product.id, quantity)}
          />
        ))}
      </ul>

      {picked && <p className="text-xs text-muted-foreground mt-1">{t("booking.products.note")}</p>}
    </div>
  );
}

function ProductCard({
  product,
  quantity,
  onChange,
}: {
  product: Product;
  quantity: number;
  onChange: (quantity: number) => void;
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const name = isRtl ? product.nameAr : product.nameEn;
  const max = Math.min(product.stockQuantity, MAX_PER_PRODUCT);
  const lowStock = product.inStock && product.stockQuantity <= 5;

  return (
    <li
      className={cn(
        "snap-start shrink-0 w-[136px] flex flex-col rounded-2xl border p-2 transition-colors",
        quantity > 0 ? "border-primary/60 bg-primary/5" : "border-border bg-card",
        !product.inStock && "opacity-60",
      )}
      data-testid={`booking-product-card-${product.id}`}
    >
      <div className="aspect-square w-full overflow-hidden rounded-xl bg-muted">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt={name} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Package className="h-8 w-8 text-muted-foreground/40" />
          </div>
        )}
      </div>

      <p className="mt-2 text-sm font-semibold leading-snug line-clamp-2 min-h-[2.5rem]">{name}</p>
      {/* bdi: sizes like "40×60 cm" keep their order inside Arabic text. */}
      <p className="text-[11px] text-muted-foreground truncate min-h-[1rem]">
        {product.size && <bdi>{product.size}</bdi>}
      </p>
      <p className="mt-0.5 text-sm">
        <span className="font-bold text-primary">{product.price.toFixed(3)}</span>{" "}
        <span className="text-[10px] text-muted-foreground">{t("services.currency")}</span>
      </p>
      <p className="text-[11px] text-muted-foreground min-h-[1rem]">
        {lowStock ? t("booking.products.lowStock", { count: product.stockQuantity }) : ""}
      </p>

      <div className="mt-auto pt-2">
        {!product.inStock ? (
          <div className="flex h-8 items-center justify-center rounded-full bg-muted text-xs font-medium text-destructive">
            {t("booking.products.outOfStock")}
          </div>
        ) : quantity === 0 ? (
          <Button
            type="button"
            variant="outline"
            className="h-8 w-full rounded-full border-primary/40 text-primary px-2 text-xs"
            onClick={() => onChange(1)}
            data-testid={`booking-product-card-add-${product.id}`}
          >
            <Plus className="h-3.5 w-3.5" />
            {t("booking.products.add")}
          </Button>
        ) : (
          <div className="flex h-8 items-center justify-between rounded-full bg-primary text-primary-foreground">
            <button
              type="button"
              aria-label={quantity > 1 ? "-" : t("booking.products.remove")}
              className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-primary-foreground/15"
              onClick={() => onChange(quantity - 1)}
              data-testid={`booking-product-card-minus-${product.id}`}
            >
              {quantity > 1 ? <Minus className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
            </button>
            <span className="text-xs font-bold tabular-nums">{quantity}</span>
            <button
              type="button"
              aria-label="+"
              className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-primary-foreground/15 disabled:opacity-40"
              disabled={quantity >= max}
              onClick={() => onChange(quantity + 1)}
              data-testid={`booking-product-card-plus-${product.id}`}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </li>
  );
}
